/**
 * How a cold-search row reads the paid Work.ua open («Відкрити контакт»).
 *
 * Everything here is derived from the three server fields on the row
 * (`workua_open_status`, `can_open_workua`, `workua_open_block`) and never
 * from the run's kind: the accumulated list does not carry the kind, and the
 * server already decided which rows the open applies to. Same rules as the
 * web admin's `lib/workuaOpen.ts`.
 */
import type { ColdSearchResult } from "@/types/domain";

/** What one open costs, as every label and confirm says it. */
export const WORKUA_OPEN_PRICE = "~25 грн";

export const WORKUA_OPEN_REQUIRED_HINT = "Спершу відкрийте контакт";

export const WORKUA_OPEN_TEXT = {
  disabled: "Відкриття ще не ввімкнене",
  capReached: "Ліміт відкриттів на сьогодні вичерпано",
  ambiguous: "Невідомо, чи списано контакт — зверніться до адміністратора",
  notAllowed: "Відкрити контакт може власник вакансії або HR Lead",
  vacancyInactive: "Вакансія не активна — контакт не відкриваємо",
  failed: "Не вдалося відкрити — контакт не списано",
} as const;

/** Re-reads while an open is in flight. The per-run read and the single row
 * of the candidate card are cheap; the accumulated read counts over the
 * vacancy's whole history, and its endpoint asks for 15 s at most. */
export const WORKUA_OPEN_POLL_MS = 5_000;
export const WORKUA_OPEN_POLL_ACCUMULATED_MS = 15_000;

/** A row the paid open applies to. Every such row carries a status, a «yes»
 * or a reason for «no»; every other row has all three at their defaults. */
export function workUaOpenApplies(row: ColdSearchResult): boolean {
  return (
    (row.workua_open_status ?? null) !== null ||
    row.can_open_workua === true ||
    (row.workua_open_block ?? null) !== null
  );
}

export function hasContacts(row: ColdSearchResult): boolean {
  return !!(row.candidate.email || row.candidate.phone_e164);
}

/** «Цікавий» and «В резерв» would hand PeopleForce a person nobody can call;
 * the server answers them 409 `workua_open_required`. */
export function needsWorkUaOpen(row: ColdSearchResult): boolean {
  return workUaOpenApplies(row) && !hasContacts(row);
}

/** Nothing left to buy: the contacts are ours, or the open already paid. */
export function workUaOpenSettledPaid(row: ColdSearchResult): boolean {
  return (
    row.workua_open_status === "opened" ||
    row.workua_open_status === "already_known"
  );
}

/** An open is on its way for this row, or for another résumé of the person. */
export function workUaOpenInFlight(row: ColdSearchResult): boolean {
  return (
    row.workua_open_status === "queued" ||
    row.workua_open_status === "calling" ||
    row.workua_open_block === "in_progress"
  );
}

/** Poll while any row on the page is still being opened. It ends on its own:
 * the server turns a `queued` / `calling` row older than ten minutes into
 * `failed` / `ambiguous`. */
export function anyWorkUaOpenInFlight(
  rows: readonly ColdSearchResult[] | undefined,
): boolean {
  return (rows ?? []).some(workUaOpenInFlight);
}

/** Why the paid button is greyed out, or `null` when nothing stops it. Only
 * the blocks that leave the button on screen; `in_progress` and `ambiguous`
 * have their own lines. */
export function workUaOpenButtonReason(row: ColdSearchResult): string | null {
  switch (row.workua_open_block) {
    case "disabled":
      return WORKUA_OPEN_TEXT.disabled;
    case "cap_reached":
      return WORKUA_OPEN_TEXT.capReached;
    case "not_allowed":
      return WORKUA_OPEN_TEXT.notAllowed;
    case "vacancy_inactive":
      return WORKUA_OPEN_TEXT.vacancyInactive;
    default:
      return null;
  }
}
